import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';
import { Box, CircularProgress, Alert } from '@mui/material';
import L from 'leaflet';

// Import configuration and theme
import { MAP_CONFIG, ZOOM_CONFIG, EUROPE_BOUNDS, EUROPE_CENTER } from '../../config/mapConfig';
import theme, {
  createD3ColorScale,
  getColorFromDynamicRange,
  calculateDynamicRanges,
  createD3QuantileScale,
  createTieredBubbleColorScale,
  createTieredBubbleGradient
} from '../../styles/theme';


// Import enhanced popup component
import CountryDetailPopup from './CountryDetailPopup';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

// Import clustering styles
import '../../styles/clustering.css';

// Country centroids for bubble positioning (ported from WorldMap)
const COUNTRY_CENTROIDS = {
  'IT': [41.8719, 12.5674],   // Italy
  'DE': [51.1657, 10.4515],   // Germany
  'FR': [46.2276, 2.2137],    // France
  'ES': [40.4637, -3.7492],   // Spain
  'GB': [55.3781, -3.4360],   // United Kingdom
  'NL': [52.1326, 5.2913],    // Netherlands
  'BE': [50.5039, 4.4699],    // Belgium
  'AT': [47.5162, 14.5501],   // Austria
  'CH': [46.8182, 8.2275],    // Switzerland
  'PL': [51.9194, 19.1343],   // Poland
  'PT': [39.3999, -8.2245],   // Portugal
  'DK': [56.2639, 9.5018],    // Denmark
  'SE': [60.1282, 18.6435],   // Sweden
  'NO': [60.4720, 8.4689],    // Norway
  'FI': [61.9241, 25.7482],   // Finland
  'IE': [53.4129, -8.2439],   // Ireland
  'CZ': [49.8175, 15.4730],   // Czech Republic
  'HU': [47.1625, 19.5033],   // Hungary
  'SK': [48.6690, 19.6990],   // Slovakia
  'SI': [46.1512, 14.9955],   // Slovenia
  'HR': [45.1000, 15.2000],   // Croatia
  'RO': [45.9432, 24.9668],   // Romania
  'BG': [42.7339, 25.4858],   // Bulgaria
  'GR': [39.0742, 21.8243],   // Greece
  'CY': [35.1264, 33.4299],   // Cyprus
  'MT': [35.9375, 14.3754],   // Malta
  'LU': [49.8153, 6.1296],    // Luxembourg
  'LV': [56.8796, 24.6032],   // Latvia
  'LT': [55.1694, 23.8813],   // Lithuania
  'EE': [58.5953, 25.0136],   // Estonia
};

// Country code mapping from various formats to our standard 2-letter codes
const COUNTRY_CODE_MAPPING = {
  // ISO_A3 to ISO_A2 mapping
  'ITA': 'IT', 'DEU': 'DE', 'FRA': 'FR', 'ESP': 'ES', 'GBR': 'GB',
  'NLD': 'NL', 'BEL': 'BE', 'AUT': 'AT', 'CHE': 'CH', 'POL': 'PL',
  'PRT': 'PT', 'DNK': 'DK', 'SWE': 'SE', 'NOR': 'NO', 'FIN': 'FI',
  'IRL': 'IE', 'CZE': 'CZ', 'HUN': 'HU', 'SVK': 'SK', 'SVN': 'SI',
  'HRV': 'HR', 'ROU': 'RO', 'BGR': 'BG', 'GRC': 'GR', 'CYP': 'CY',
  'MLT': 'MT', 'LUX': 'LU', 'LVA': 'LV', 'LTU': 'LT', 'EST': 'EE',

  // Country names to ISO_A2 mapping
  'Germany': 'DE', 'France': 'FR', 'Spain': 'ES', 'Italy': 'IT',
  'Portugal': 'PT', 'Netherlands': 'NL', 'Belgium': 'BE', 'Austria': 'AT',
  'Switzerland': 'CH', 'Poland': 'PL', 'Denmark': 'DK', 'Sweden': 'SE',
  'Norway': 'NO', 'Finland': 'FI', 'Ireland': 'IE', 'Czech Republic': 'CZ',
  'Hungary': 'HU', 'Slovakia': 'SK', 'Slovenia': 'SI', 'Croatia': 'HR',
  'Romania': 'RO', 'Bulgaria': 'BG', 'Greece': 'GR', 'Cyprus': 'CY',
  'Malta': 'MT', 'Luxembourg': 'LU', 'Latvia': 'LV', 'Lithuania': 'LT',
  'Estonia': 'EE',

  // Alternative codes and variations
  'UK': 'GB', 'United Kingdom': 'GB', 'Great Britain': 'GB',
  'Czech Rep.': 'CZ', 'Czechia': 'CZ',

  // Handle potential variations in the Europe GeoJSON
  'Russian Federation': 'RU', 'Russia': 'RU', 'RUS': 'RU',
  'Ukraine': 'UA', 'UKR': 'UA',
  'Belarus': 'BY', 'BLR': 'BY',
  'Turkey': 'TR', 'TUR': 'TR',
  'Serbia': 'RS', 'SRB': 'RS',
  'Bosnia and Herzegovina': 'BA', 'BIH': 'BA',
  'Montenegro': 'ME', 'MNE': 'ME',
  'Albania': 'AL', 'ALB': 'AL',
  'North Macedonia': 'MK', 'Macedonia': 'MK', 'MKD': 'MK',
  'Moldova': 'MD', 'MDA': 'MD',
  'Iceland': 'IS', 'ISL': 'IS'
};

// Helper function to normalize country codes
const normalizeCountryCode = (feature) => {
  const props = feature.properties;

  // For Europe GeoJSON, prioritize country name mapping first since it's most reliable
  const countryName = props.name || props.NAME || props.NAME_EN;

  // First try to map by country name (most reliable)
  if (countryName && COUNTRY_CODE_MAPPING[countryName]) {
    return COUNTRY_CODE_MAPPING[countryName];
  }

  // Try different property fields in order of preference
  const possibleCodes = [
    props.wb_a2,        // World Bank 2-letter code (most reliable for Europe)
    props.WB_A2,        // Alternative case
    props.iso_a2_eh,    // ISO A2 Enhanced (lowercase)
    props.ISO_A2_EH,    // ISO A2 Enhanced
    props.adm0_a3,      // Administrative 3-letter code
    props.ADM0_A3,      // Alternative case
    props.ISO_A3,       // ISO A3
    props.brk_a3,       // Break away 3-letter code
    props.gu_a3,        // Geounit 3-letter code
    props.su_a3,        // Subunit 3-letter code
    props.postal,       // Postal code (single letter, needs mapping)
    props.ISO_A2,       // ISO A2 (often -99 in this file)
    countryName,        // Country name as fallback
    feature.id
  ].filter(code => code && code !== '-99' && code !== -99); // Filter out -99 values

  for (const code of possibleCodes) {
    // If it's already a 2-letter code and not a placeholder, use it directly
    if (code && code.length === 2 && code !== '-99') {
      return code.toUpperCase();
    }
    // If it's a single letter (postal code), try to map it
    if (code && code.length === 1) {
      // Handle single letter postal codes
      const singleLetterMapping = {
        'F': 'FR', 'D': 'DE', 'I': 'IT', 'E': 'ES', 'P': 'PT', 'A': 'AT',
        'B': 'BE', 'N': 'NL', 'S': 'SE', 'L': 'LU', 'H': 'HU', 'G': 'GR'
      };
      if (singleLetterMapping[code.toUpperCase()]) {
        return singleLetterMapping[code.toUpperCase()];
      }
    }
    // If it's a 3-letter code or name, try to map it
    if (code && COUNTRY_CODE_MAPPING[code]) {
      return COUNTRY_CODE_MAPPING[code];
    }
  }

  // Debug: log unmapped countries
  if (possibleCodes.length > 0) {
    console.log(`[EuropeMap] Could not map country:`, {
      name: countryName,
      codes: possibleCodes,
      properties: props
    });
  }

  return possibleCodes[0] || null;
};

/**
 * ZoomHandler Component - Enhanced with debounced zoom handling
 */
const ZoomHandler = ({ onZoomChange, onDebouncedZoomChange }) => {
  const debounceTimeoutRef = useRef(null);
  const lastZoomRef = useRef(null);
  const performanceStartRef = useRef(null);

  const map = useMapEvents({
    zoomstart: () => {
      performanceStartRef.current = performance.now();
    },
    zoom: () => {
      // Immediate zoom updates for real-time feedback
      const currentZoom = map.getZoom();
      if (lastZoomRef.current !== currentZoom) {
        lastZoomRef.current = currentZoom;
        onZoomChange(currentZoom);
      }
    },
    zoomend: () => {
      const currentZoom = map.getZoom();
      const performanceEnd = performance.now();
      const zoomDuration = performanceEnd - (performanceStartRef.current || performanceEnd);

      // Production: Reduced logging - only log if performance is poor
      if (zoomDuration > 500) {
        console.warn(`[ZoomHandler] Slow zoom transition: ${zoomDuration.toFixed(2)}ms to level ${currentZoom}`);
      }

      // Clear existing debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Reduced debounce for better synchronization (50ms instead of 100ms)
      debounceTimeoutRef.current = setTimeout(() => {
        onDebouncedZoomChange(currentZoom);
      }, 50);

      // Immediate zoom change for UI updates
      onZoomChange(currentZoom);
    },
  });

  // Set initial zoom on mount
  useEffect(() => {
    const initialZoom = map.getZoom();
    lastZoomRef.current = initialZoom;
    onZoomChange(initialZoom);
    onDebouncedZoomChange(initialZoom);
  }, [map, onZoomChange, onDebouncedZoomChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

/**
 * Get dominant status from a group of markers based on in-progress percentages
 */
const getDominantStatus = (markers) => {
  if (!markers || markers.length === 0) return 'low';

  // Calculate average in-progress percentage
  const totalPercentage = markers.reduce((sum, marker) => {
    const cityData = marker.options?.cityData;
    return sum + (cityData?.in_progress_percentage || 0);
  }, 0);

  const avgPercentage = totalPercentage / markers.length;

  // Determine status based on average percentage
  if (avgPercentage >= 75) return 'critical';
  if (avgPercentage >= 50) return 'high';
  if (avgPercentage >= 25) return 'medium';
  return 'low';
};

/**
 * Create uniform bubble-style cluster icon (same style as country markers)
 */
const createStatusBasedClusterIcon = (cluster, currentZoom) => {
  const markers = cluster.getAllChildMarkers();
  const count = cluster.getChildCount();

  // Use smaller bubble style for better shadow visibility
  const uniformRadius = 16; // Reduced from 20 for better shadow visibility

  // Apply minimal zoom-responsive scaling
  const zoomFactor = Math.pow(1.1, currentZoom - ZOOM_CONFIG.DEFAULT_ZOOM);
  const scaledRadius = uniformRadius * zoomFactor;

  // Apply size bounds: minimum 12px, maximum 24px (reduced for better shadow)
  const bubbleRadius = Math.max(12, Math.min(24, scaledRadius));

  // Use uniform color for all clusters - same as country bubbles
  const uniformColor = '#0d3475'; // Brand blue color

  // Use white text for consistency
  const textColor = '#ffffff';

  // Scale font size with bubble size
  const fontSize = Math.max(8, Math.min(14, bubbleRadius * 0.4));

  const svgIcon = `
    <svg width="${bubbleRadius * 3.5}" height="${bubbleRadius * 3.5}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="cluster-shadow-gradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="${uniformColor}" stop-opacity="1.0"/>
          <stop offset="50%" stop-color="#1e4a7a" stop-opacity="0.8"/>
          <stop offset="100%" stop-color="#1e4a7a" stop-opacity="0.2"/>
        </radialGradient>
        <filter id="cluster-blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5"/>
        </filter>
      </defs>
      <!-- Shadow circle with gradient and blur -->
      <circle
        cx="${bubbleRadius * 1.75}"
        cy="${bubbleRadius * 1.75}"
        r="${bubbleRadius + 10}"
        fill="url(#cluster-shadow-gradient)"
        filter="url(#cluster-blur)"
      />
      <!-- Main circle -->
      <circle
        cx="${bubbleRadius * 1.75}"
        cy="${bubbleRadius * 1.75}"
        r="${bubbleRadius - 1}"
        fill="${uniformColor}"
        stroke="${uniformColor}"
        stroke-width="1"
      />
      <text
        x="${bubbleRadius * 1.75}"
        y="${bubbleRadius * 1.75}"
        text-anchor="middle"
        dominant-baseline="central"
        font-size="${Math.max(10, Math.min(16, fontSize + 2))}"
        font-weight="normal"
        fill="${textColor}"
        style="text-shadow: 0 1px 2px rgba(0,0,0,0.5); pointer-events: none;"
      >
        ${count}
      </text>
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    className: 'cluster-icon-clean',
    iconSize: [bubbleRadius * 2, bubbleRadius * 2],
    iconAnchor: [bubbleRadius, bubbleRadius],
    popupAnchor: [0, -bubbleRadius]
  });
};

/**
 * EuropeMap Component
 * Leaflet-based interactive European map with choropleth visualization
 * Features:
 * - Zoom-based data handling (country vs city display)
 * - Choropleth coloring based on service appointment density
 * - Smooth hover effects and click handlers
 * - Responsive design with Material-UI theme integration
 * - D3-powered color scaling
 * - Enhanced transitions and performance optimizations (Phase 3)
 */
const EuropeMap = ({
  onCountryHover = () => { },
  onCountryClick = () => { },
  height = "100%",
  focusOnEurope = true,
  refreshInterval = 3600000, // 5 minutes default
  // Data props from useSharedTicketData
  countryMapData = [],
  cityMapData = [],
  storeLocationData = [],
  countryAggregatedData = [],
  summaryData = {},
  loading = false,
  error = null
}) => {
  // Derived values for backward compatibility
  const lastUpdated = summaryData?.last_updated || null;
  const hasData = countryMapData.length > 0 || countryAggregatedData.length > 0;
  const debugInfo = {
    countryCount: countryMapData.length,
    cityCount: cityMapData.length,
    storeCount: storeLocationData.length,
    hasData
  };

  // Map state with enhanced zoom handling
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(ZOOM_CONFIG.DEFAULT_ZOOM);
  const [debouncedZoom, setDebouncedZoom] = useState(ZOOM_CONFIG.DEFAULT_ZOOM);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const geoJsonRef = useRef(null);

  // Performance monitoring and caching
  const markerCacheRef = useRef(new Map());
  const transitionTimeoutRef = useRef(null);
  const lastTransitionRef = useRef(null);
  const performanceMetricsRef = useRef({
    transitionCount: 0,
    averageTransitionTime: 0,
    lastTransitionTime: 0
  });

  // Create a map of country codes to data for quick lookup (using enhanced data)
  const dataMap = useMemo(() => {
    const map = {};
    countryAggregatedData.forEach(item => {
      map[item.country_code] = item;
    });
    return map;
  }, [countryAggregatedData]);

  // Calculate dynamic ranges for multicolor heatmap (using enhanced data)
  const dynamicRanges = useMemo(() => {
    if (countryAggregatedData.length === 0) return [];
    const ranges = calculateDynamicRanges(countryAggregatedData, 7);
    // Production: Reduced logging
    return ranges;
  }, [countryAggregatedData]);

  // Calculate bubble size scale using enhanced data with real API totals
  const bubbleScale = useMemo(() => {
    if (countryAggregatedData.length === 0) return d3.scaleLinear().domain([0, 1]).range([0, 10]);

    const values = countryAggregatedData.map(d => d.open_count || 0).filter(v => v > 0);
    if (values.length === 0) return d3.scaleLinear().domain([0, 1]).range([0, 10]);

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    // Production: Reduced logging

    // Scale bubble radius from 12px to 20px (same as WorldMap)
    return d3.scaleLinear()
      .domain([minValue, maxValue])
      .range([12, 20]);
  }, [countryAggregatedData]);

  // Create tiered bubble color scale for country bubbles (using enhanced data)
  const tieredColorScale = useMemo(() => {
    return createTieredBubbleColorScale(countryAggregatedData);
  }, [countryAggregatedData]);

  // Calculate font size for bubble labels based on bubble size
  const getBubbleFontSize = useCallback((ticketCount) => {
    const bubbleRadius = bubbleScale(ticketCount);
    // Font size scales with bubble size, with min 8px and max 16px
    return Math.max(8, Math.min(16, bubbleRadius * 0.35));
  }, [bubbleScale]);

  // Phase 2: Use real store location data with exact coordinates from API
  const storeDataWithCoordinates = useMemo(() => {
    if (!storeLocationData || storeLocationData.length === 0) {
      // Production: Reduced logging
      return [];
    }

    // Filter out stores with invalid coordinates
    const validStores = storeLocationData.filter(store => {
      const hasValidCoords = store.coordinates &&
        Array.isArray(store.coordinates) &&
        store.coordinates.length === 2 &&
        !isNaN(store.coordinates[0]) &&
        !isNaN(store.coordinates[1]);

      if (!hasValidCoords) {
        console.warn(`[EuropeMap] Invalid coordinates for store ${store.id}:`, store.coordinates);
      }

      return hasValidCoords;
    });

    // Production: Reduced logging
    return validStores;
  }, [storeLocationData]);

  // Create uniform bubble marker icon for countries
  const createBubbleIcon = useCallback((countryData, currentZoom) => {
    // Use smaller size for better shadow visibility
    const uniformRadius = 14; // Reduced from 16 for better shadow visibility

    // Apply minimal zoom-responsive scaling (much less variation)
    const zoomFactor = Math.pow(1.1, currentZoom - ZOOM_CONFIG.DEFAULT_ZOOM);
    const scaledRadius = uniformRadius * zoomFactor;

    // Apply tighter size bounds for uniformity: minimum 10px, maximum 20px (reduced)
    const bubbleRadius = Math.max(10, Math.min(20, scaledRadius));

    // Use uniform font size
    const fontSize = Math.max(8, Math.min(12, bubbleRadius * 0.4));

    // Use uniform color for all country bubbles - specified brand color
    const uniformColor = '#0d3475'; // Brand blue color

    // Use white text for consistency
    const textColor = '#ffffff';

    const svgIcon = `
      <svg width="${bubbleRadius * 3.5}" height="${bubbleRadius * 3.5}" xmlns="http://www.w3.org/2000/svg" style="overflow: visible;">
        <defs>
          <radialGradient id="bubble-shadow-gradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="${uniformColor}" stop-opacity="1.0"/>
            <stop offset="50%" stop-color="#1e4a7a" stop-opacity="0.8"/>
            <stop offset="100%" stop-color="#1e4a7a" stop-opacity="0.2"/>
          </radialGradient>
          <filter id="bubble-blur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="1.5"/>
          </filter>
        </defs>
        <!-- Shadow circle with gradient and blur -->
        <circle
          cx="${bubbleRadius * 1.75}"
          cy="${bubbleRadius * 1.75}"
          r="${bubbleRadius + 10}"
          fill="url(#bubble-shadow-gradient)"
          filter="url(#bubble-blur)"
        />
        <!-- Main circle -->
        <circle
          cx="${bubbleRadius * 1.75}"
          cy="${bubbleRadius * 1.75}"
          r="${bubbleRadius - 1}"
          fill="${uniformColor}"
          stroke="${uniformColor}"
          stroke-width="1"
        />
        <text
          x="${bubbleRadius * 1.75}"
          y="${bubbleRadius * 1.75}"
          text-anchor="middle"
          dominant-baseline="central"
          font-size="${Math.max(10, Math.min(14, fontSize + 2))}"
          font-weight="normal"
          fill="${textColor}"
          style="text-shadow: 0 1px 2px rgba(0,0,0,0.5); pointer-events: none;"
        >
          ${countryData.open_count}
        </text>
      </svg>
    `;

    return L.divIcon({
      html: svgIcon,
      className: 'bubble-marker-clean',
      iconSize: [bubbleRadius * 2, bubbleRadius * 2],
      iconAnchor: [bubbleRadius, bubbleRadius],
    });
  }, []);

  // Create geolocation pin icon for individual stores
  const createStoreIcon = useCallback((storeData, currentZoom) => {
    // Use uniform color for all pins - same as bubbles and clusters
    const uniformColor = '#0d3475'; // Brand blue color - same as bubbles

    // Apply zoom-responsive scaling for pin size
    const zoomFactor = Math.pow(1.1, currentZoom - ZOOM_CONFIG.THRESHOLDS.CITY_VISIBLE);
    const baseSize = 20; // Base pin size
    const scaledSize = baseSize * zoomFactor;

    // Apply size bounds: minimum 16px, maximum 32px
    const pinSize = Math.max(16, Math.min(32, scaledSize));
    const pinWidth = pinSize;
    const pinHeight = pinSize * 1.2; // Pin is taller than it is wide

    // Create geolocation pin SVG
    const svgIcon = `
      <svg width="${pinWidth}" height="${pinHeight}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 29">
        <!-- Pin shape -->
        <path
          d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 17 12 17s12-9.8 12-17c0-6.6-5.4-12-12-12z"
          fill="${uniformColor}"
          stroke="#ffffff"
          stroke-width="1"
        />
        <!-- Inner circle -->
        <circle
          cx="12"
          cy="12"
          r="4"
          fill="#ffffff"
          fill-opacity="0.9"
        />
        <!-- Status indicator dot -->
        <circle
          cx="12"
          cy="12"
          r="2"
          fill="${uniformColor}"
        />
      </svg>
    `;

    return L.divIcon({
      html: svgIcon,
      className: 'store-pin-marker',
      iconSize: [pinWidth, pinHeight],
      iconAnchor: [pinWidth / 2, pinHeight], // Anchor at bottom center of pin
      popupAnchor: [0, -pinHeight], // Popup appears above the pin
      // Store data for clustering system
      cityData: storeData // Keep same property name for clustering compatibility
    });
  }, []);

  // Handle feature events - completely disable country interactions for clean look
  const onEachFeature = useCallback((feature, layer) => {
    // Remove all event handlers to prevent any hover effects
    // Countries are now purely visual with no interactions
  }, []);

  // Enhanced zoom change handlers with performance monitoring
  const handleZoomChange = useCallback((newZoom) => {
    setZoom(newZoom);

    // Track transition state
    const wasTransitioning = isTransitioning;
    const shouldTransition = (newZoom < ZOOM_CONFIG.THRESHOLDS.CITY_VISIBLE) !== (zoom < ZOOM_CONFIG.THRESHOLDS.CITY_VISIBLE);

    if (shouldTransition && !wasTransitioning) {
      setIsTransitioning(true);
      lastTransitionRef.current = performance.now();

      // Clear transition timeout if exists
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      // Reduced transition timeout for better synchronization (200ms instead of 300ms)
      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);

        // Update performance metrics
        const transitionTime = performance.now() - lastTransitionRef.current;
        const metrics = performanceMetricsRef.current;
        metrics.transitionCount++;
        metrics.lastTransitionTime = transitionTime;
        metrics.averageTransitionTime = ((metrics.averageTransitionTime * (metrics.transitionCount - 1)) + transitionTime) / metrics.transitionCount;

        // Production: Only log if performance is poor
        if (transitionTime > 500) {
          console.warn(`[EuropeMap] Slow transition: ${transitionTime.toFixed(2)}ms (avg: ${metrics.averageTransitionTime.toFixed(2)}ms)`);
        }
      }, 200);
    }
  }, [zoom, isTransitioning]);

  // Debounced zoom change handler for expensive operations
  const handleDebouncedZoomChange = useCallback((newZoom) => {
    setDebouncedZoom(newZoom);
    // Production: Reduced logging
  }, []);

  // MapContainer event handlers
  const handleMapCreated = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  // Cleanup effect for transitions and timeouts
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  // Enhanced marker caching and cleanup system
  const clearMarkerCache = useCallback(() => {
    markerCacheRef.current.clear();
    // Production: Reduced logging
  }, []);

  // Handle zoom changes for marker recreation with optimized debouncing
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      // Production: Reduced logging

      // Clear cache when crossing zoom thresholds to prevent memory leaks
      const crossedThreshold = (debouncedZoom < ZOOM_CONFIG.THRESHOLDS.CITY_VISIBLE) !== (zoom < ZOOM_CONFIG.THRESHOLDS.CITY_VISIBLE);
      if (crossedThreshold) {
        clearMarkerCache();
      }
    }, 50);

    return () => clearTimeout(debounceTimeout);
  }, [debouncedZoom, zoom, clearMarkerCache]);

  // Determine what data to show based on zoom level with enhanced logic
  const shouldShowCountries = useMemo(() => {
    return debouncedZoom < ZOOM_CONFIG.THRESHOLDS.COUNTRY_ONLY;
  }, [debouncedZoom]);

  const shouldShowCities = useMemo(() => {
    return debouncedZoom >= ZOOM_CONFIG.THRESHOLDS.CITY_VISIBLE;
  }, [debouncedZoom]);

  // Viewport-based filtering for performance optimization
  const getVisibleStores = useMemo(() => {
    if (!map || !shouldShowCities || !storeDataWithCoordinates.length) {
      return storeDataWithCoordinates;
    }

    try {
      const bounds = map.getBounds();
      const visibleStores = storeDataWithCoordinates.filter(store => {
        const [lat, lng] = store.coordinates;
        return bounds.contains([lat, lng]);
      });

      console.log(`[EuropeMap] Viewport filtering: ${visibleStores.length}/${storeDataWithCoordinates.length} stores visible`);
      return visibleStores;
    } catch (error) {
      console.warn('[EuropeMap] Viewport filtering failed, using all stores:', error);
      return storeDataWithCoordinates;
    }
  }, [map, shouldShowCities, storeDataWithCoordinates, debouncedZoom]);

  // Loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={height}
        bgcolor={theme.colors.background.primary}
      >
        <CircularProgress
          size={60}
          sx={{ color: theme.colors.primary[500] }}
        />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={height}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Failed to load map data. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      width="100%"
      height={height}
      bgcolor={theme.colors.background.primary}
      overflow="hidden"
      sx={{
        '& .leaflet-container': {
          height: '100%',
          width: '100%',
          backgroundColor: theme.colors.background.primary,
        },
        '& .leaflet-control-zoom': {
          border: 'none !important',
          boxShadow: theme.shadows.md,
        },
        '& .leaflet-control-zoom a': {
          backgroundColor: `${theme.colors.background.card} !important`,
          color: `${theme.colors.text.primary} !important`,
          border: `1px solid ${theme.colors.border.light} !important`,
          '&:hover': {
            backgroundColor: `${theme.colors.background.secondary} !important`,
          }
        },
        // Clean bubble marker styles - remove all backgrounds and borders
        '& .bubble-marker-clean': {
          background: 'none !important',
          backgroundColor: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.1)',
            zIndex: 1000
          }
        },
        // Store pin marker styles (updated for geolocation pins)
        '& .store-pin-marker': {
          background: 'none !important',
          backgroundColor: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.1)',
            zIndex: 1000
          }
        },
        // Legacy store marker styles (keep for compatibility)
        '& .store-marker': {
          background: 'none !important',
          backgroundColor: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.2)',
            zIndex: 1000
          }
        },
        // Remove default Leaflet marker styling
        '& .leaflet-marker-icon': {
          background: 'none !important',
          backgroundColor: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important'
        },
        // Completely disable country interactions and hover effects
        '& .country-feature-neutral': {
          cursor: 'default !important',
          transition: 'none !important',
          pointerEvents: 'none !important'
        },
        // Override any default Leaflet interactive styles
        '& .leaflet-interactive': {
          cursor: 'default !important',
          '&:hover': {
            cursor: 'default !important'
          }
        },
        // Cluster-specific styles with enhanced clean styling
        '& .cluster-critical, & .cluster-high, & .cluster-medium, & .cluster-low, & .cluster-icon-clean': {
          background: 'none !important',
          backgroundColor: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.1)',
            zIndex: 1000
          }
        },
        // Fix for Leaflet cluster default styling
        '& .leaflet-marker-icon.cluster-icon-clean': {
          background: 'none !important',
          backgroundColor: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          borderRadius: '0 !important'
        },
        // Override any default cluster styling
        '& .leaflet-cluster-anim .leaflet-marker-icon': {
          background: 'none !important',
          backgroundColor: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important'
        },
        // Enhanced popup styles
        '& .enhanced-popup': {
          '& .leaflet-popup-content-wrapper': {
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.xl,
            border: `1px solid ${theme.colors.border.light}`,
            padding: 0
          },
          '& .leaflet-popup-content': {
            margin: 0,
            padding: 0,
            borderRadius: theme.borderRadius.lg,
            overflow: 'hidden'
          },
          '& .leaflet-popup-tip': {
            backgroundColor: theme.colors.background.primary,
            border: `1px solid ${theme.colors.border.light}`,
            borderTop: 'none',
            borderRight: 'none'
          },
          '& .leaflet-popup-close-button': {
            padding: '8px',
            right: '8px',
            top: '8px',
            width: '34px',          /* Add explicit width */
            height: '34px',         /* Add explicit height */
            display: 'flex',        /* Use flexbox for centering */
            alignItems: 'center',  /* Center vertically */
            justifyContent: 'center', /* Center horizontally */
            lineHeight: 1,       /* Reset line-height */
          }
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{ width: '100%', height: '100%' }}
      >
        <MapContainer
          center={EUROPE_CENTER}
          zoom={ZOOM_CONFIG.DEFAULT_ZOOM}
          minZoom={ZOOM_CONFIG.MIN_ZOOM}
          maxZoom={ZOOM_CONFIG.MAX_ZOOM}
          maxBounds={EUROPE_BOUNDS}
          maxBoundsViscosity={1.0}
          style={{ height: '100%', width: '100%' }}
          whenCreated={handleMapCreated}
          zoomControl={true}
          scrollWheelZoom={true}
          doubleClickZoom={true}
          dragging={true}
        >
          {/* Enhanced Zoom Handler with debouncing */}
          <ZoomHandler
            onZoomChange={handleZoomChange}
            onDebouncedZoomChange={handleDebouncedZoomChange}
          />

          {/* Tile Layer */}
          <TileLayer
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />

          {/* Country-Level Bubble Markers (zoom < 6) - Enhanced with smooth transitions */}
          <AnimatePresence mode="wait" initial={false}>
            {!shouldShowCities && (
              <motion.div
                key="country-markers"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                  opacity: isTransitioning ? 0.7 : 1,
                  scale: isTransitioning ? 0.9 : 1,
                  transition: {
                    duration: 0.2,
                    ease: "easeInOut",
                    staggerChildren: 0.03
                  }
                }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  transition: { duration: 0.2, ease: "easeInOut" }
                }}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
              >
                {countryAggregatedData.map((countryData) => {
                  const centroid = COUNTRY_CENTROIDS[countryData.country_code];
                  if (!centroid || !countryData.open_count || countryData.open_count === 0) return null;

                  return (
                    <motion.div
                      key={`country-motion-${countryData.country_code}`}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      style={{ pointerEvents: 'auto' }}
                    >
                      <Marker
                        key={`enhanced-bubble-${countryData.country_code}-${debouncedZoom}`}
                        position={centroid}
                        icon={createBubbleIcon(countryData, debouncedZoom)}
                        eventHandlers={{
                          mouseover: () => onCountryHover(countryData),
                          click: () => onCountryClick(countryData),
                        }}
                      >
                        <CountryDetailPopup
                          countryData={countryData}
                          zoomLevel={debouncedZoom}
                          showRegionalBreakdown={true}
                        />
                      </Marker>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Store-Level Clustered Markers (zoom >= 6) - Enhanced with smooth transitions and viewport filtering */}
          <AnimatePresence mode="wait" initial={false}>
            {shouldShowCities && (
              <motion.div
                key="store-markers"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{
                  opacity: isTransitioning ? 0.7 : 1,
                  scale: isTransitioning ? 1.05 : 1,
                  transition: {
                    duration: 0.2,
                    ease: "easeInOut",
                    delay: 0.05 // Reduced delay for better synchronization
                  }
                }}
                exit={{
                  opacity: 0,
                  scale: 1.1,
                  transition: { duration: 0.2, ease: "easeInOut" }
                }}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              >
                <MarkerClusterGroup
                  key={`cluster-group-${debouncedZoom}`}
                  chunkedLoading
                  maxClusterRadius={(currentZoom) => {
                    // Optimized clustering logic with smaller radii for better performance
                    if (currentZoom < 7) return 80;
                    if (currentZoom < 8) return 60;
                    if (currentZoom < 9) return 40;
                    return 30;
                  }}
                  iconCreateFunction={(cluster) => createStatusBasedClusterIcon(cluster, debouncedZoom)}
                  showCoverageOnHover={false}
                  spiderfyOnMaxZoom={true}
                  removeOutsideVisibleBounds={true}
                  disableClusteringAtZoom={ZOOM_CONFIG.THRESHOLDS.CLUSTER_BREAK}
                  animate={false}
                  animateAddingMarkers={false}
                  spiderfyDistanceMultiplier={1.2}
                  polygonOptions={{
                    fillColor: 'transparent',
                    color: 'transparent',
                    weight: 0,
                    opacity: 0,
                    fillOpacity: 0
                  }}
                >
                  {getVisibleStores.map((store) => (
                    <Marker
                      key={`store-${store.id}-${debouncedZoom}`}
                      position={store.coordinates}
                      icon={createStoreIcon(store, debouncedZoom)}
                      eventHandlers={{
                        click: () => {
                          console.log(`[EuropeMap] Store clicked: ${store.id} in ${store.country_name} at [${store.coordinates[0]}, ${store.coordinates[1]}]`);
                          // TODO: Add store click handler
                        },
                      }}
                    >
                      <Popup>
                        <Box p={1.5}>
                          <Box fontWeight="bold" mb={1} color="text.primary">
                            {store.store_name || 'Store Location'}
                          </Box>
                          <Box fontSize="0.875rem" color="text.secondary" mb={1}>
                            {store.country_name}
                          </Box>

                          <Box
                            sx={{
                              mt: 1,
                              height: 4,
                              bgcolor: theme.colors.neutral[200],
                              borderRadius: theme.borderRadius.full,
                              overflow: 'hidden'
                            }}
                          >

                          </Box>
                        </Box>
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Debug Info for Current View - Production: Removed */}
        </MapContainer>
      </motion.div>

      {/* Enhanced Debug Info with Performance Metrics - Production: Removed */}
    </Box>
  );
};

EuropeMap.propTypes = {
  onCountryHover: PropTypes.func,
  onCountryClick: PropTypes.func,
  height: PropTypes.string,
  focusOnEurope: PropTypes.bool,
  refreshInterval: PropTypes.number,
  // Data props from useSharedTicketData
  countryMapData: PropTypes.array,
  cityMapData: PropTypes.array,
  storeLocationData: PropTypes.array,
  countryAggregatedData: PropTypes.array,
  summaryData: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object
};

export default EuropeMap;